import { useState } from 'react'
import { Check } from 'lucide-react'
import { Link } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import {
  TIER_ACCENT,
  TIER_BRAND_LABEL,
  TIER_MEMBER_CAP,
  TIER_ADMIN_CAP,
  TIER_MONTHLY_PRICE,
  TIER_ANNUAL_PRICE,
  annualSavingPercent,
} from '@/lib/squadron/tierMeta'
import type { SubscriptionTier } from '@/lib/api/schemas'
import { ctaClicked } from '@/lib/analytics'

type TierCard = {
  tier: SubscriptionTier
  blurb: string
  highlights: string[]
}

// Feature highlights are governed by contracts/TIER_CAPABILITIES.md (golden).
// Do not add a bullet that isn't a granted capability there.
const TIER_CARDS: TierCard[] = [
  {
    tier: 'FREE',
    blurb: 'Get your squad in and running. No card, no catch.',
    highlights: [
      'Roster, events & availability',
      'Discord bot + event sync',
      'Verified org status',
      'Monthly squadron report',
    ],
  },
  {
    tier: 'LEVEL_1',
    blurb:
      'Start charging dues and running real tournaments. Your squad starts looking the part.',
    highlights: [
      'Everything in FREE',
      'Tournaments — up to 25 players',
      'Member fees + revenue dashboard',
      'Custom logo + Tournament Stadium',
    ],
  },
  {
    tier: 'LEVEL_2',
    blurb: 'Bigger roster, cross-squadron play, more room to run the show.',
    highlights: [
      'Everything in BRONZE',
      'Tournaments — up to 50 players',
      'Cross-squadron tournaments (soon)',
      '4 public events / month',
    ],
  },
  {
    tier: 'LEVEL_3',
    blurb: 'For full organisations: paid pots, top billing, the works.',
    highlights: [
      'Everything in SILVER',
      'Membership tiers — Supporter / Champion / Legend',
      'Paid prize-pool tournaments',
      'Tournaments up to 300 · priority support',
    ],
  },
]

type Interval = 'monthly' | 'annual'

export function PricingTierCards() {
  const [interval, setInterval] = useState<Interval>('monthly')

  return (
    <div>
      {/* Monthly / annual toggle */}
      <div className="flex justify-center">
        <div className="inline-flex items-center gap-1 rounded-full border border-border bg-bg/40 p-1">
          <ToggleButton
            active={interval === 'monthly'}
            onClick={() => setInterval('monthly')}
          >
            Monthly
          </ToggleButton>
          <ToggleButton
            active={interval === 'annual'}
            onClick={() => setInterval('annual')}
          >
            Annual
            <span className="font-display ml-1.5 rounded-sm bg-success/15 px-1.5 py-[1px] text-[9px] font-extrabold tracking-[0.18em] text-success uppercase">
              Save ~16%
            </span>
          </ToggleButton>
        </div>
      </div>

      {/* Cards */}
      <div className="mt-8 grid gap-4 lg:grid-cols-4">
        {TIER_CARDS.map((card) => (
          <TierCardView key={card.tier} card={card} interval={interval} />
        ))}
      </div>

      <p className="font-display mt-6 text-center text-[11px] text-fg-subtle">
        Prices in GBP · Stripe handles the billing · cancel anytime, no
        commitment
      </p>
    </div>
  )
}

function TierCardView({
  card,
  interval,
}: {
  card: TierCard
  interval: Interval
}) {
  const accent = TIER_ACCENT[card.tier]
  const monthlyPrice = TIER_MONTHLY_PRICE[card.tier]
  const annualPrice = TIER_ANNUAL_PRICE[card.tier]
  const annualSaving = annualSavingPercent(card.tier)
  const featured = card.tier === 'LEVEL_2'

  const headlinePrice =
    interval === 'annual' ? annualPrice : monthlyPrice
  const headlineSuffix = card.tier === 'FREE' ? null : interval === 'annual' ? '/year' : '/month'

  return (
    <div
      className="relative flex flex-col rounded-2xl border bg-surface/40 p-5"
      style={{
        borderColor: featured ? accent : 'var(--color-border, #2a2a4d)',
        boxShadow: featured ? `0 0 40px -16px ${accent}` : undefined,
      }}
    >
      {featured && (
        <span
          className="font-display absolute -top-3 left-1/2 -translate-x-1/2 rounded-full border bg-bg px-3 py-1 text-[10px] font-extrabold tracking-[0.22em] uppercase"
          style={{ borderColor: accent, color: accent }}
        >
          Most picked
        </span>
      )}

      {/* Tier label */}
      <p
        className="font-display text-[11px] font-extrabold tracking-[0.3em] uppercase"
        style={{ color: accent }}
      >
        {TIER_BRAND_LABEL[card.tier]}
      </p>

      {/* Price */}
      <div className="mt-3 flex items-baseline gap-1.5">
        <span className="font-display text-4xl font-black tabular-nums text-fg">
          £{headlinePrice}
        </span>
        {headlineSuffix && (
          <span className="font-display text-xs text-fg-muted">{headlineSuffix}</span>
        )}
      </div>
      {interval === 'annual' && annualSaving > 0 && (
        <p className="mt-1 text-[10px] text-success">
          vs £{monthlyPrice * 12} paying monthly — save ~{annualSaving}%
        </p>
      )}
      {card.tier === 'FREE' && (
        <p className="mt-1 text-[10px] text-fg-subtle">no card, no trial expiry</p>
      )}

      {/* Caps */}
      <div className="mt-4 space-y-1 border-t border-border/60 pt-4">
        <CapRow label="Members" value={`up to ${TIER_MEMBER_CAP[card.tier]}`} />
        <CapRow label="Admins" value={`${TIER_ADMIN_CAP[card.tier]}`} />
      </div>

      {/* Blurb */}
      <p className="mt-4 text-xs text-fg-muted">{card.blurb}</p>

      {/* Highlights */}
      <ul className="mt-4 flex-1 space-y-2 text-xs text-fg">
        {card.highlights.map((h) => (
          <li key={h} className="flex items-start gap-2">
            <Check className="mt-0.5 size-3.5 shrink-0 text-success" />
            <span>{h}</span>
          </li>
        ))}
      </ul>

      {/* CTA */}
      <div className="mt-5">
        <Button
          asChild
          variant={featured ? 'primary' : 'outline'}
          size="md"
          className="w-full"
        >
          <Link
            to="/signup"
            onClick={() => ctaClicked('get_started', 'pricing', '/signup')}
          >
            {card.tier === 'FREE' ? 'Start free' : `Pick ${TIER_BRAND_LABEL[card.tier]}`}
          </Link>
        </Button>
      </div>
    </div>
  )
}

function CapRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="font-display text-[10px] tracking-[0.18em] text-fg-subtle uppercase">
        {label}
      </span>
      <span className="font-display text-xs font-bold tabular-nums text-fg">
        {value}
      </span>
    </div>
  )
}

function ToggleButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`font-display inline-flex items-center rounded-full px-4 py-1.5 text-xs font-bold tracking-wider transition-colors ${
        active
          ? 'bg-primary text-white'
          : 'text-fg-muted hover:text-fg'
      }`}
    >
      {children}
    </button>
  )
}
